import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const AuroraBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;
    const currentMount = mountRef.current;

    const dpr = Math.min(window.devicePixelRatio, 2);
    const numOctaves = 5;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'low-power' });
    renderer.setPixelRatio(dpr);

    const setRendererSize = (w: number, h: number) => {
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = `${w}px`;
      renderer.domElement.style.height = `${h}px`;
    };

    setRendererSize(window.innerWidth, window.innerHeight);
    renderer.domElement.style.position = 'fixed';
    renderer.domElement.style.top = '0';
    renderer.domElement.style.left = '0';
    renderer.domElement.style.zIndex = '0';
    renderer.domElement.style.display = 'block';
    currentMount.appendChild(renderer.domElement);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      },
      vertexShader: `void main() { gl_Position = vec4(position, 1.0); }`,
      fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;

        #define NUM_OCTAVES ${numOctaves}

        float rand(vec2 n) {
          return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 ip = floor(p);
          vec2 u = fract(p);
          u = u * u * (3.0 - 2.0 * u);
          float res = mix(
            mix(rand(ip), rand(ip + vec2(1.0, 0.0)), u.x),
            mix(rand(ip + vec2(0.0, 1.0)), rand(ip + vec2(1.0, 1.0)), u.x),
            u.y
          );
          return res * res;
        }

        float fbm(vec2 x) {
          float v = 0.0;
          float a = 0.5;
          vec2 shift = vec2(100.0);
          mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
          for (int i = 0; i < NUM_OCTAVES; ++i) {
            v += a * noise(x);
            x = rot * x * 2.0 + shift;
            a *= 0.6;
          }
          return v;
        }

        void main() {
          vec2 uv = gl_FragCoord.xy / iResolution.xy;
          float t = iTime * 0.35;

          vec2 wind = vec2(t * 0.25, t * 0.04);

          vec2 q = vec2(0.0);
          q.x = fbm(uv + wind + 0.08 * t);
          q.y = fbm(uv + wind + vec2(1.0));

          vec2 r = vec2(0.0);
          r.x = fbm(uv + 1.0 * q + vec2(1.7, 9.2) + 0.30 * t + wind);
          r.y = fbm(uv + 1.0 * q + vec2(8.3, 2.8) + 0.27 * t + wind);

          float f = fbm(uv + r);

          vec3 deepBlack  = vec3(0.015, 0.012, 0.008);
          vec3 darkRust   = vec3(0.15, 0.055, 0.015);
          vec3 brandOrange = vec3(0.76, 0.33, 0.10);
          vec3 warmAmber   = vec3(0.90, 0.50, 0.15);

          float rightBias = smoothstep(0.2, 1.0, uv.x);
          float intensityBoost = 1.0 + rightBias * 0.9;

          vec3 col = mix(deepBlack, darkRust, clamp(f * f * 2.2 * intensityBoost, 0.0, 1.0));
          col = mix(col, brandOrange * 0.5, clamp(length(q) * 0.5 * intensityBoost, 0.0, 1.0));
          col = mix(col, warmAmber * 0.35, clamp(length(r.x) * 0.35 * intensityBoost, 0.0, 1.0));

          col = mix(deepBlack, col, clamp((f * f * f + 0.65 * f * f + 0.55 * f) * 0.7 * intensityBoost, 0.0, 1.0));

          vec2 vigCenter = vec2(0.65, 0.5);
          float vignette = 1.0 - smoothstep(0.5, 1.3, length(uv - vigCenter) * 0.9);
          col *= (0.75 + 0.25 * vignette);

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    let lastFrame = 0;
    let animationFrameId: number;

    const animate = (now: number = 0) => {
      animationFrameId = requestAnimationFrame(animate);
      const delta = lastFrame === 0 ? 16 : now - lastFrame;
      lastFrame = now;
      material.uniforms.iTime.value += delta * 0.0003;
      renderer.render(scene, camera);
    };

    // Ignore height-only resizes (mobile URL bar collapse causes constant resize jitter)
    let lastWidth = window.innerWidth;
    let resizeTimer: number | undefined;
    const handleResize = () => {
      const w = window.innerWidth;
      if (w === lastWidth) return; // skip address-bar height changes
      lastWidth = w;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const h = window.innerHeight;
        setRendererSize(w, h);
        material.uniforms.iResolution.value.set(w, h);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    material.uniforms.iResolution.value.set(window.innerWidth, window.innerHeight);
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(resizeTimer);
      if (currentMount.contains(renderer.domElement)) {
        currentMount.removeChild(renderer.domElement);
      }
      renderer.dispose();
      material.dispose();
      geometry.dispose();
    };
  }, []);

  return <div ref={mountRef} className="fixed inset-0 z-0" />;
};

export { AuroraBackground };
